/**
 * Reusable Navigation Functions
 * Functions for managing navigation bars with indicators, chevrons, and auto-centering
 */

/**
 * Updates the tab indicator position relative to wrapper
 * @param {number} index - Index of the active tab
 * @param {HTMLElement} tabsContainer - The tabs container element
 * @param {HTMLElement} tabIndicator - The indicator element
 */
function updateTabIndicator(index, tabsContainer, tabIndicator) {
  
  const activeButton = tabsContainer.children[index];
  if (!activeButton || !tabIndicator) return;

  const containerWidth = tabsContainer.clientWidth;
  const containerScrollWidth = tabsContainer.scrollWidth;
  const buttonLeft = activeButton.offsetLeft;
  const buttonWidth = activeButton.offsetWidth;
  const buttonHeight = activeButton.offsetHeight;
  const buttonCenter = buttonLeft + buttonWidth / 2;
  
  // Calculate where the button will be after centering scroll completes
  const desiredScrollLeft = buttonCenter - containerWidth / 2;
  const maxScroll = containerScrollWidth - containerWidth;
  const finalScrollLeft = Math.max(0, Math.min(desiredScrollLeft, maxScroll));
  
  // Check if the button can actually be centered
  const canBeCentered = (desiredScrollLeft >= 0 && desiredScrollLeft <= maxScroll);
  
  if (canBeCentered && containerScrollWidth > containerWidth) {
    // Button will be centered - keep indicator centered in wrapper
    const wrapperWidth = tabsContainer.parentElement.clientWidth;
    const centerPosition = wrapperWidth / 2;
    tabIndicator.style.left = centerPosition + 'px';
    tabIndicator.style.transform = 'translate(-50%, -50%)';
  } else {
    // Button cannot be centered - calculate final position after scroll
    const wrapperRect = tabsContainer.parentElement.getBoundingClientRect();
    const tabsRect = tabsContainer.getBoundingClientRect();
    
    // Calculate where button will be relative to wrapper after scroll completes
    const buttonLeftAfterScroll = buttonLeft - finalScrollLeft;
    const tabsLeftRelativeToWrapper = tabsRect.left - wrapperRect.left;
    const finalButtonPosition = tabsLeftRelativeToWrapper + buttonLeftAfterScroll + buttonWidth/2;
    
    tabIndicator.style.left = finalButtonPosition + 'px';
    tabIndicator.style.transform = 'translate(-50%, -50%)';
  }
  
  // Always update width and height
  tabIndicator.style.width = buttonWidth + 'px';
  tabIndicator.style.height = buttonHeight + 'px';
  
  // Also set CSS custom property for fallback
  tabsContainer.style.setProperty('--button-height', buttonHeight + 'px');
}

/**
 * Centers the active button in the scrollable container
 * @param {number} index - Index of the button to center
 * @param {HTMLElement} tabsContainer - The tabs container element
 */
function centerActiveButton(index, tabsContainer) {
  const activeButton = tabsContainer.children[index];
  if (!activeButton) return;

  const containerWidth = tabsContainer.clientWidth;
  const containerScrollWidth = tabsContainer.scrollWidth;
  
  // Only scroll if content is actually scrollable
  if (containerScrollWidth <= containerWidth) return;

  const buttonLeft = activeButton.offsetLeft;
  const buttonWidth = activeButton.offsetWidth;
  const buttonCenter = buttonLeft + buttonWidth / 2;
  
  // Calculate scroll position to center the button
  const scrollLeft = buttonCenter - containerWidth / 2;
  
  // Ensure we don't scroll beyond bounds
  const maxScroll = containerScrollWidth - containerWidth;
  const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScroll));
  
  tabsContainer.scrollTo({
    left: clampedScrollLeft,
    behavior: 'smooth'
  });
}

/**
 * Updates chevron visibility and border radius based on scroll position or selected index
 * @param {HTMLElement} tabsContainer - The tabs container element
 * @param {HTMLElement} leftChevron - The left chevron element
 * @param {HTMLElement} rightChevron - The right chevron element
 * @param {number} selectedIndex - Optional. If provided, uses predictive logic for this index
 */
function updateChevronVisibility(tabsContainer, leftChevron, rightChevron, selectedIndex = null) {
  const containerWidth = tabsContainer.clientWidth;
  const containerScrollWidth = tabsContainer.scrollWidth;
  const scrollLeft = tabsContainer.scrollLeft;
  
  // Only show chevrons if content is scrollable
  if (containerScrollWidth <= containerWidth) {
    if (leftChevron) leftChevron.classList.remove('visible');
    if (rightChevron) rightChevron.classList.remove('visible');
    return;
  }

  // Get computed border radius from tabs container
  const computedStyle = window.getComputedStyle(tabsContainer);
  const borderRadius = computedStyle.borderRadius || computedStyle.borderTopLeftRadius || '0px';
  
  // Apply matching border radius to chevrons
  if (leftChevron) {
    leftChevron.style.borderTopLeftRadius = borderRadius;
    leftChevron.style.borderBottomLeftRadius = borderRadius;
    leftChevron.style.borderTopRightRadius = '0px';
    leftChevron.style.borderBottomRightRadius = '0px';
  }
  
  if (rightChevron) {
    rightChevron.style.borderTopRightRadius = borderRadius;
    rightChevron.style.borderBottomRightRadius = borderRadius;
    rightChevron.style.borderTopLeftRadius = '0px';
    rightChevron.style.borderBottomLeftRadius = '0px';
  }

  const totalTabs = tabsContainer.children.length;
  const maxScroll = containerScrollWidth - containerWidth;

  // Case 1: Index provided and it's the first tab (index 0)
  if (selectedIndex !== null && selectedIndex === 0) {
    if (leftChevron) leftChevron.classList.remove('visible');
    if (rightChevron) rightChevron.classList.add('visible');
  }
  // Case 2: Index provided and it's the last tab
  else if (selectedIndex !== null && selectedIndex === totalTabs - 1) {
    if (leftChevron) leftChevron.classList.add('visible');
    if (rightChevron) rightChevron.classList.remove('visible');
  }
  // Case 3: Everything else (middle tabs with index, or no index provided)
  else {
    
    let effectiveScrollLeft = scrollLeft;
    
    // If we have a middle tab index, calculate expected scroll position
    if (selectedIndex !== null) {
      const selectedButton = tabsContainer.children[selectedIndex];
      if (selectedButton) {
        const buttonLeft = selectedButton.offsetLeft;
        const buttonWidth = selectedButton.offsetWidth;
        const buttonCenter = buttonLeft + buttonWidth / 2;
        
        // Calculate where scroll will end up when this button is centered
        const desiredScrollLeft = buttonCenter - containerWidth / 2;
        effectiveScrollLeft = Math.max(0, Math.min(desiredScrollLeft, maxScroll));
      }
    }
    
    // Show/hide left chevron based on effective scroll position
    if (effectiveScrollLeft > 0) {
      if (leftChevron) leftChevron.classList.add('visible');
    } else {
      if (leftChevron) leftChevron.classList.remove('visible');
    }

    // Show/hide right chevron based on effective scroll position
    if (effectiveScrollLeft < maxScroll - 1) { // -1 for subpixel precision
      if (rightChevron) rightChevron.classList.add('visible');
    } else {
      if (rightChevron) rightChevron.classList.remove('visible');
    }
  }
}

/**
 * Navigates to the next or previous tab
 * @param {string} direction - 'next' or 'prev'
 * @param {HTMLElement} tabsContainer - The tabs container element
 * @param {Array} items - Array of items (for length calculation)
 * @param {Function} selectTabCallback - Callback function to select a tab
 * @param {HTMLElement} leftChevron - The left chevron element
 * @param {HTMLElement} rightChevron - The right chevron element
 */
function navigateTab(direction, tabsContainer, items, selectTabCallback, leftChevron, rightChevron) {
  const activeButton = tabsContainer.querySelector('.navigation-toggle-button.active');
  if (!activeButton) return;

  const currentIndex = parseInt(activeButton.dataset.index);
  let newIndex;

  if (direction === 'next') {
    newIndex = Math.min(currentIndex + 1, items.length - 1);
  } else {
    newIndex = Math.max(currentIndex - 1, 0);
  }

  if (newIndex !== currentIndex && selectTabCallback) {
    updateChevronVisibility(tabsContainer, leftChevron, rightChevron, newIndex);
    selectTabCallback(newIndex);
  }
}

/**
 * Sets up navigation functionality for a navigation bar
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.tabsContainer - The tabs container element
 * @param {HTMLElement} config.tabIndicator - The indicator element
 * @param {HTMLElement} config.leftChevron - The left chevron element
 * @param {HTMLElement} config.rightChevron - The right chevron element
 * @param {Array} config.items - Array of navigation items
 * @param {Function} config.selectTabCallback - Callback function to select a tab
 * @returns {Object} Object with navigation functions bound to the config
 */
function setupNavigation(config) {
  const {
    tabsContainer,
    tabIndicator,
    leftChevron,
    rightChevron,
    items,
    selectTabCallback
  } = config;

  // Bound functions with config parameters
  const boundFunctions = {
    updateTabIndicator: (index) => updateTabIndicator(index, tabsContainer, tabIndicator),
    centerActiveButton: (index) => centerActiveButton(index, tabsContainer),
    updateChevronVisibility: (selectedIndex = null) => updateChevronVisibility(tabsContainer, leftChevron, rightChevron, selectedIndex),
    navigateTab: (direction) => navigateTab(direction, tabsContainer, items, selectTabCallback, leftChevron, rightChevron),
    recompute: () => updateChevronVisibility(tabsContainer, leftChevron, rightChevron),
    recomputeForIndex: (index) => updateChevronVisibility(tabsContainer, leftChevron, rightChevron, index)
  };

  // Set up event listeners
  if (leftChevron) {
    leftChevron.addEventListener('click', () => {
      boundFunctions.navigateTab('prev');
      // Trigger recompute after navigation
    });
  }
  if (rightChevron) {
    rightChevron.addEventListener('click', () => {
      boundFunctions.navigateTab('next');
      // Trigger recompute after navigation
    });
  }

  // Set up scroll and resize listeners
  if (tabsContainer) {
    const recompute = () => {
      const activeButton = tabsContainer.querySelector('.navigation-toggle-button.active');
      if (activeButton) {
        const activeIndex = parseInt(activeButton.dataset.index);
        boundFunctions.updateChevronVisibility(activeIndex);
      }
    };

    // Initial computation
    recompute();


    tabsContainer.addEventListener("scroll", recompute, { passive: true });

    // On resize
    try { 
      new ResizeObserver(recompute).observe(tabsContainer); 
    } catch (_) {}

    // On window resize
    /*
    window.addEventListener("resize", () => {
      recompute();
      // Update indicator position on resize
      const activeButton = tabsContainer.querySelector('.navigation-toggle-button.active');
      if (activeButton) {
        const activeIndex = parseInt(activeButton.dataset.index);
        setTimeout(() => boundFunctions.updateTabIndicator(activeIndex), 100);
      }
    });
    */
  }

  return boundFunctions;
}

// Export functions for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    updateTabIndicator,
    centerActiveButton,
    updateChevronVisibility,
    navigateTab,
    setupNavigation
  };
}
